import { useQuery } from "@tanstack/react-query";
import { Calendar as CalIcon, MapPin, Users } from "lucide-react";
import { fetchEvents } from "@/lib/api";
import { Reveal, Stagger, StaggerItem } from "./motion";
import { scrollTo } from "./Header";

const CAT_COLORS = {
  "Parent Clinic": "#E0619B",
  Workshop: "#38A4E0",
  Talk: "#F0862F",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-IE", {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export const Events = () => {
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["events"],
    queryFn: fetchEvents,
  });

  return (
    <section
      id="events"
      data-testid="events-section"
      className="relative mx-auto max-w-7xl px-6 lg:px-10 py-28 lg:py-36"
    >
      <Reveal>
        <p
          className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.28em] mb-5"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          <span className="h-1.5 w-6 rounded-full prism-line" /> Events &amp; Workshops
        </p>
        <h2 className="font-serif font-light tracking-tight leading-tight text-primary text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
          Come and learn together
        </h2>
      </Reveal>

      {isLoading ? (
        <p className="mt-12 text-text-secondary" style={{ color: "hsl(var(--muted-foreground))" }}>
          Loading events…
        </p>
      ) : (
        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((ev) => (
            <StaggerItem key={ev.id}>
              <article
                data-testid={`event-card-${ev.id}`}
                className="group h-full flex flex-col justify-between rounded-[1.6rem] border border-border bg-card p-8 hover:border-primary/40 hover:-translate-y-1 transition-all duration-500 overflow-hidden relative"
              >
                <span
                  className="absolute inset-x-0 top-0 h-1"
                  style={{ background: CAT_COLORS[ev.category] || "#2FD6B8" }}
                />
                <div>
                  <span
                    className="inline-block rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider"
                    style={{
                      color: CAT_COLORS[ev.category] || "#2FD6B8",
                      background: `${CAT_COLORS[ev.category] || "#2FD6B8"}1f`,
                    }}
                  >
                    {ev.category}
                  </span>
                  <h3 className="mt-5 font-serif text-2xl font-light tracking-tight text-primary leading-snug">
                    {ev.title}
                  </h3>
                  <p className="mt-3 text-sm font-light leading-relaxed" style={{ color: "hsl(var(--muted-foreground))" }}>
                    {ev.description}
                  </p>
                </div>
                <div className="mt-6 space-y-2 border-t border-border pt-5 text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>
                  <div className="flex items-center gap-2">
                    <CalIcon size={15} /> {fmtDate(ev.date)}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={15} /> {ev.location}
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={15} /> {ev.spots}
                  </div>
                </div>
                <button
                  data-testid={`event-book-${ev.id}`}
                  onClick={() => scrollTo("booking")}
                  className="mt-6 w-full rounded-full border border-primary/30 py-3 text-sm text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                >
                  Reserve a place
                </button>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      )}
    </section>
  );
};
